import { DateInputLayoutProps } from 'framework/form';
import { DATE_FORMAT } from 'framework/utils';

const clampTwoDigits = (value: string, max: number) => {
  if (value.length < 2) {
    return value;
  }

  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    return '';
  }

  return String(Math.min(parsed, max)).padStart(2, '0');
};

export const formatDateValue = (
  rawValue: string,
  { dateFormat, timeFormat }: Pick<DateInputLayoutProps, 'dateFormat' | 'timeFormat'>,
) => {
  const shouldAutoFormatDateInput = (dateFormat || DATE_FORMAT.INPUT_VALUE) === DATE_FORMAT.INPUT_VALUE;
  if (!shouldAutoFormatDateInput) {
    return rawValue;
  }

  const normalizedRawValue = rawValue.trimStart();
  const [datePart, ...restParts] = normalizedRawValue.split(' ');
  const dateDigits = datePart.replace(/\D/g, '').slice(0, 8);

  if (!dateDigits) {
    return '';
  }

  let formattedDate = dateDigits;
  if (dateDigits.length > 4) {
    formattedDate = `${dateDigits.slice(0, 4)}-${dateDigits.slice(4)}`;
  }
  if (dateDigits.length > 6) {
    formattedDate = `${dateDigits.slice(0, 4)}-${dateDigits.slice(4, 6)}-${dateDigits.slice(6)}`;
  }

  const rawTimeValue = restParts.join('').replace(/\D/g, '');
  if (!timeFormat) {
    return formattedDate;
  }

  const timeDigits = rawTimeValue.slice(0, 4);

  if (!timeDigits) {
    return dateDigits.length >= 8 ? `${formattedDate} ` : formattedDate;
  }

  const hour = clampTwoDigits(timeDigits.slice(0, 2), 23);
  const minutes = clampTwoDigits(timeDigits.slice(2, 4), 59);

  let formattedTime = hour;
  if (minutes) {
    formattedTime = `${hour}:${minutes}`;
  }

  return `${formattedDate} ${formattedTime}`;
};

const getDigitsCountBeforeCaret = (value: string, caretPosition = 0) =>
  value.slice(0, Math.max(0, caretPosition)).replace(/\D/g, '').length;

const getCaretPositionAfterDigits = (value: string, digitsCount: number) => {
  if (digitsCount <= 0) return 0;

  let seenDigits = 0;
  for (const [index, char] of [...value].entries()) {
    if (/\d/.test(char) && ++seenDigits === digitsCount) {
      return index + 1;
    }
  }

  return value.length;
};

export const formatDateValueWithCaret = (
  rawValue: string,
  options: Pick<DateInputLayoutProps, 'dateFormat' | 'timeFormat'>,
  selectionStart: number | null,
  selectionEnd: number | null,
) => {
  const value = formatDateValue(rawValue, options);

  const getSelectionPosition = (position: number | null) => {
    if (position === null) return value.length;

    const digitsCount = getDigitsCountBeforeCaret(rawValue, position);

    return getCaretPositionAfterDigits(value, digitsCount);
  };

  return {
    value,
    selectionStart: getSelectionPosition(selectionStart),
    selectionEnd: getSelectionPosition(selectionEnd),
  };
};
